import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    CalendarDays,
    Users,
    Building2,
    UserCog,
    SlidersHorizontal,
    LogOut,
    ShieldCheck,
    BookOpen,
    Clock,
    GraduationCap,
    Menu,
    X,
    UserCircle,
    Network,
    Newspaper,
    Sparkles,
    LineChart,
    FlaskConical,
    Briefcase,
    ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CostTrackerWidget from "@/components/CostTrackerWidget";
import LevelBadge from "@/components/LevelBadge";
import CommandPalette, { CommandPaletteTrigger } from "@/components/CommandPalette";
import GlobalSmartBot from "@/components/GlobalSmartBot";
import FloatingNewsWidget from "@/components/FloatingNewsWidget";
import { useNewsUnread } from "@/hooks/useNewsUnread";
import { getRoleDisplay } from "@/lib/roleLabel";
import { ProfileOnboarding } from "@/components/ProfileOnboarding";
import { useUsageTracking, moduleFor } from "@/lib/usageTracker";
import { BarChart3 } from "lucide-react";

/** Experimental features popover — anchored next to the "Experiment" badge
 * on the Quiz & Facts sidebar item. Renders in a portal so it's never clipped
 * by the sidebar's `overflow-y-auto`.
 */
const ExperimentPopover = ({ anchorRef, popRef, onClose, navigate }) => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return null;
    // Position: to the right of the sidebar badge, vertically centered on the row
    const style = {
        position: "fixed",
        top: Math.max(8, rect.top - 8),
        left: rect.right + 12,
        zIndex: 60,
    };
    const experiments = [
        {
            title: "Quiz & Facts",
            state: "Live",
            summary: "Interactive flashcards on industry stats, with daily insights and XP rewards.",
            path: "/research",
        },
    ];
    return (
        <div
            ref={popRef}
            style={style}
            data-testid="experiment-popover"
            className="w-80 rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden"
        >
            <div className="px-4 py-3 bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white">
                <div className="flex items-center gap-2">
                    <FlaskConical className="h-4 w-4" />
                    <div className="font-heading font-bold text-sm tracking-tight">What&rsquo;s experimenting?</div>
                </div>
                <div className="text-[11px] opacity-90 mt-0.5">
                    Early-access features. Expect rapid change — your feedback shapes them.
                </div>
            </div>
            <div className="p-2 space-y-1">
                {experiments.map((x) => (
                    <button
                        key={x.path}
                        type="button"
                        onClick={() => { onClose(); navigate(x.path); }}
                        data-testid={`experiment-item-${x.path.replace("/", "")}`}
                        className="w-full text-left rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors group"
                    >
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-slate-900 group-hover:text-fuchsia-700 transition-colors">
                                {x.title}
                            </span>
                            <span className="inline-flex items-center px-1.5 h-4 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase tracking-wider border border-emerald-100">
                                {x.state}
                            </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1 leading-snug">{x.summary}</div>
                    </button>
                ))}
            </div>
            <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
                <div className="text-[10px] text-slate-500">
                    Got ideas? Ping the Odyssey team.
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="experiment-popover-close"
                    className="text-[10px] font-semibold text-slate-500 hover:text-slate-800 uppercase tracking-wider"
                >
                    Close
                </button>
            </div>
        </div>
    );
};

const Layout = () => {
    const { user, setUser, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Pending-learning badge — count of resources assigned to me that I
    // haven't started yet. Refetched whenever the user navigates to or
    // away from the Learning Hub so the count stays in sync as they
    // mark progress in the tab.
    const [learningPending, setLearningPending] = useState(0);
    const fetchLearningPending = useCallback(async () => {
        try {
            const r = await api.get("/learning/summary");
            setLearningPending(r.data?.not_started || 0);
        } catch {
            setLearningPending(0);
        }
    }, []);
    useEffect(() => {
        if (!user) return;
        fetchLearningPending();
    }, [user, location.pathname, fetchLearningPending]);

    // News unread badge — shares its data source with the floating news
    // orb so the count is always in sync (one poll for both surfaces).
    const { unseenCount: newsUnread } = useNewsUnread();

    // Experimental features popover — tap the "Experiment" badge next to
    // Quiz & Facts to see what's currently in the sandbox.
    const [expOpen, setExpOpen] = useState(false);
    const expBadgeRef = React.useRef(null);
    const expPopRef = React.useRef(null);
    useEffect(() => {
        if (!expOpen) return;
        const onDoc = (e) => {
            if (expPopRef.current?.contains(e.target)) return;
            if (expBadgeRef.current?.contains(e.target)) return;
            setExpOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setExpOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [expOpen]);
    // Close popover on route change
    useEffect(() => { setExpOpen(false); }, [location.pathname]);

    // Close drawer on route change
    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
    // Lock body scroll when drawer is open
    useEffect(() => {
        document.body.style.overflow = drawerOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [drawerOpen]);

    const navGroups = [
        {
            id: "overview",
            label: "Overview",
            color: "#6D5DF3",
            items: [
                { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", tint: "#6D5DF3" },
                { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", tint: "#F59E0B" },
                { to: "/org", label: "Organisation Tree", icon: Network, testid: "nav-org", tint: "#0EA5E9" },
            ],
        },
        {
            id: "learning",
            label: "Learning & Knowledge",
            color: "#2EB4AC",
            items: [
                { to: "/learning", label: "Learning Hub", icon: BookOpen, testid: "nav-learning", tint: "#2EB4AC" },
                { to: "/projects", label: "Projects", icon: Briefcase, testid: "nav-projects", tint: "#EA580C" },
                { to: "/news", label: "News & Updates", icon: Newspaper, testid: "nav-news", tint: "#E11D48" },
                { to: "/research", label: "Quizzes & Facts", icon: LineChart, testid: "nav-research", tint: "#A855F7" },
            ],
        },
        {
            id: "attendance",
            label: "Attendance",
            color: "#0EA5E9",
            items: [
                { to: "/attendance", label: "Monthly Attendance", icon: Clock, testid: "nav-attendance", tint: "#0EA5E9" },
                { to: "/kno", label: "KNO Attendance", icon: GraduationCap, testid: "nav-kno", tint: "#059669" },
            ],
        },
        {
            id: "profile",
            label: "My Profile",
            color: "#F97316",
            items: [
                { to: user?.user_id ? `/employees/${user.user_id}` : "/profile", label: "Profile", icon: UserCircle, testid: "nav-profile", tint: "#F97316" },
                ...(user?.role === "employee" ? [{ to: "/usage", label: "My Usage", icon: BarChart3, testid: "nav-usage", tint: "#3B82F6" }] : []),
                { to: "/activity", label: "Activity", icon: Sparkles, testid: "nav-activity", tint: "#EC4899" },
            ],
        },
        {
            id: "organization",
            label: "Organization",
            color: "#8B5CF6",
            restricted: true,
            items: [
                { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees", tint: "#8B5CF6" },
                { to: "/departments", label: "Departments", icon: Building2, testid: "nav-departments", tint: "#0891B2" },
                { to: "/managers", label: "Managers", icon: UserCog, testid: "nav-managers", tint: "#DC2626" },
            ],
        },
        {
            id: "admin",
            label: "Admin",
            color: "#64748B",
            restricted: true,
            items: [
                { to: "/control-panel", label: "Control Panel", icon: SlidersHorizontal, testid: "nav-control-panel", tint: "#64748B" },
            ],
        },
    ];

    useUsageTracking(location.pathname, moduleFor(location.pathname, navGroups), !!user?.user_id);

    const roleBadge = getRoleDisplay(user);

    // Dev role-switcher: visible only to users whose REAL role is
    // super_admin (i.e. user.actual_role). The toggle cycles through
    // super_admin → employee → manager → super_admin so a developer
    // sweekar can preview the app from any perspective without logging
    // out / impersonating. The override is per-session and reset by a
    // fresh login.
    const isActualSuperAdmin = user?.actual_role === "super_admin";
    const [switchingRole, setSwitchingRole] = useState(false);
    const switchRoleTo = async (target) => {
        if (switchingRole) return;
        setSwitchingRole(true);
        try {
            const r = await api.post("/auth/role-override", { role: target });
            setUser(r.data);
            const label = target ? (target === "employee" ? "Employee" : "Manager") : "Super Admin";
            toast.success(`Now viewing as ${label}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to switch role");
        } finally {
            setSwitchingRole(false);
        }
    };

    const initials = (user?.name || user?.email || "?")
        .split(/[\s@]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("");

    // ── Collapsible nav groups — default state is EXPANDED so users see
    //    everything at a glance; toggle state is remembered in
    //    localStorage. `undefined` means "never touched → default open".
    const [openGroups, setOpenGroups] = useState(() => {
        try {
            const raw = localStorage.getItem("wv_nav_open_groups");
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    useEffect(() => {
        try { localStorage.setItem("wv_nav_open_groups", JSON.stringify(openGroups)); } catch {}
    }, [openGroups]);
    const toggleGroup = (id) => setOpenGroups((s) => ({ ...s, [id]: s[id] === false }));
    /* `isGroupOpen` treats `undefined` (never toggled) as OPEN. Once the
     *   user closes a group, `false` sticks; reopening restores `true`. */
    const isGroupOpen = (id) => openGroups[id] !== false;

    // ── Desktop sidebar rail: 64 px icon-only by default; hovering the
    //    rail (or focusing any of its buttons) expands it to a 264 px
    //    labelled overlay without reflowing the main content. The overlay
    //    hover-intent uses `railHover` + a small delay to avoid flicker.
    const [railHover, setRailHover] = useState(false);
    const railTimer = React.useRef(null);
    const openRail = () => {
        if (railTimer.current) { clearTimeout(railTimer.current); railTimer.current = null; }
        setRailHover(true);
    };
    const closeRail = () => {
        railTimer.current = setTimeout(() => setRailHover(false), 120);
    };
    useEffect(() => () => { if (railTimer.current) clearTimeout(railTimer.current); }, []);
    // Collapse the rail on route change so the labels aren't lingering
    // after the user picks a destination.
    useEffect(() => { setRailHover(false); }, [location.pathname]);

    // ── Header slide-up on scroll: sticky topbar hides when scrolling
    //    down (>60 px past the fold), returns immediately on any upward
    //    gesture. Wheel + touch both feed the same setter so it feels
    //    responsive on trackpads and phones. Kept in Layout so every
    //    page inherits the behaviour without opting in.
    const [headerHidden, setHeaderHidden] = useState(false);
    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            const goingDown = y > lastY + 4;
            const goingUp   = y < lastY - 4;
            if (goingDown && y > 60) setHeaderHidden(true);
            else if (goingUp) setHeaderHidden(false);
            lastY = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const SidebarInner = ({ onItemClick, collapsed = false }) => (
        <>
            <button
                onClick={() => { navigate("/dashboard"); onItemClick?.(); }}
                className={`flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-6"} h-16 border-b border-slate-200 hover:bg-slate-50 transition-colors w-full`}
                aria-label="Odyssey home"
            >
                <div className="h-9 w-9 rounded-lg bg-[#2EB4AC] text-white flex items-center justify-center font-heading font-bold shrink-0">O</div>
                {!collapsed && (
                    <div className="text-left leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-lg tracking-tight">Odyssey</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">by Wellversed</div>
                    </div>
                )}
            </button>

            <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-5 space-y-5 overflow-y-auto scrollbar-thin`}>
                {navGroups.map((group) => {
                    // Hide restricted groups (Organization, Admin) from
                    // pure employees. Indirect managers — role="employee"
                    // but with at least one direct report — still see the
                    // Organization group so they can navigate to their
                    // team's timelines, attendance, etc.
                    const isPureEmployee = user?.role === "employee" && !user?.has_reports;
                    // Admin section stays super-admin only regardless.
                    if (group.id === "admin" && user?.role !== "super_admin") return null;
                    if (group.restricted && isPureEmployee) return null;
                    return (
                        <div key={group.id} data-testid={`nav-group-${group.id}`}>
                            {!collapsed && (
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group.id)}
                                    className="w-full flex items-center justify-between px-3 mb-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold hover:text-slate-700 transition-colors group/hd"
                                    data-testid={`nav-group-head-${group.id}`}
                                >
                                    <span className="flex items-center gap-1.5">
                                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: group.color }} aria-hidden />
                                        {group.label}
                                    </span>
                                    <ChevronDown className={`h-3 w-3 opacity-60 group-hover/hd:opacity-100 transition-transform ${isGroupOpen(group.id) ? "rotate-0" : "-rotate-90"}`} />
                                </button>
                            )}
                            {(collapsed || isGroupOpen(group.id)) && (
                            <div className={`${collapsed ? "space-y-1" : "rounded-xl bg-slate-50/70 border border-slate-200/70 p-1.5 space-y-0.5"}`}>
                                {group.items.map((item) => (
                                    <React.Fragment key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        data-testid={item.testid}
                                        end={item.to === "/dashboard" || item.to === "/learning"}
                                        onClick={() => onItemClick?.()}
                                        title={collapsed ? item.label : undefined}
                                        className={({ isActive }) =>
                                            `group flex items-center ${collapsed ? "justify-center h-10 px-0" : "gap-3 px-2.5 h-9"} rounded-lg text-sm font-medium transition-all ${
                                                isActive
                                                    ? "bg-gradient-to-r from-[#2EB4AC]/15 via-[#2EB4AC]/8 to-transparent text-[#25908A] shadow-[inset_3px_0_0_#2EB4AC] font-semibold"
                                                    : "text-slate-500 hover:bg-white hover:text-slate-900 opacity-75 hover:opacity-100"
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <span className="relative inline-flex">
                                                    <span
                                                        className={`relative inline-flex items-center justify-center h-7 w-7 rounded-xl transition-all overflow-hidden ${isActive ? "shadow-md ring-1 ring-white/70" : "group-hover:shadow-sm"}`}
                                                        style={isActive
                                                            ? { background: `linear-gradient(135deg, ${item.tint}, ${item.tint}DD 55%, ${item.tint}88)`, color: "#fff", boxShadow: `0 6px 18px ${item.tint}55` }
                                                            : { background: `linear-gradient(135deg, ${item.tint || "#94A3B8"}22, ${item.tint || "#94A3B8"}0A)`, color: item.tint || "#64748B" }}
                                                        aria-hidden
                                                    >
                                                        <item.icon className="h-3.5 w-3.5 relative z-[1]" />
                                                        {isActive && (
                                                            <span
                                                                aria-hidden
                                                                className="absolute inset-0 rounded-xl opacity-70"
                                                                style={{ background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.55), transparent 60%)` }}
                                                            />
                                                        )}
                                                    </span>
                                                    {/* Pulsing teal dot on the News icon when there
                                                        are unread top stories — visible even when
                                                        the user can't fit the full count badge. */}
                                                    {item.to === "/news" && newsUnread > 0 && (
                                                        <>
                                                            <span aria-hidden className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#2EB4AC] ring-2 ring-white" />
                                                            <span aria-hidden className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#2EB4AC] animate-ping opacity-70" />
                                                        </>
                                                    )}
                                                </span>
                                                {!collapsed && (
                                                <span className={`${item.to === "/research" || item.to === "/learning" ? "whitespace-nowrap" : ""} flex-1`}>{item.label}</span>
                                                )}
                                                {!collapsed && item.to === "/learning" && (
                                                    <span
                                                        data-testid="nav-learning-newlook-badge"
                                                        title="Revamped Learning Hub — a fresh, futuristic experience"
                                                        className="lh-newlook-pill shrink-0 inline-flex items-center gap-0.5 px-1 h-4 rounded-full text-white text-[8px] font-bold uppercase tracking-widest leading-none"
                                                    >
                                                        <Sparkles className="h-2 w-2" />
                                                        New
                                                    </span>
                                                )}
                                                {!collapsed && item.to === "/research" && (
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        ref={expBadgeRef}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setExpOpen((v) => !v);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setExpOpen((v) => !v);
                                                            }
                                                        }}
                                                        data-testid="nav-research-exploratory-badge"
                                                        title="Experimental feature — tap to learn more"
                                                        className="shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white shadow-sm ring-1 ring-white/40 hover:ring-white/80 hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
                                                    >
                                                        <FlaskConical className="h-2.5 w-2.5" />
                                                    </span>
                                                )}
                                                {!collapsed && item.to === "/learning" && learningPending > 0 && (
                                                    <span
                                                        aria-hidden
                                                        data-testid="nav-learning-badge"
                                                        title={`${learningPending} ${learningPending === 1 ? "resource" : "resources"} waiting for you`}
                                                        className="h-1.5 w-1.5 rounded-full bg-[#2EB4AC] shadow-[0_0_0_3px_rgba(46,180,172,0.15)]"
                                                    />
                                                )}
                                                {!collapsed && item.to === "/news" && newsUnread > 0 && (
                                                    <span
                                                        data-testid="nav-news-badge"
                                                        title={`${newsUnread} new ${newsUnread === 1 ? "story" : "stories"}`}
                                                        className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#2EB4AC] text-white text-[10px] font-bold leading-none"
                                                    >
                                                        {newsUnread > 9 ? "9+" : newsUnread}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </NavLink>
                                    </React.Fragment>
                                ))}
                            </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {expOpen && expBadgeRef.current && createPortal(
                <ExperimentPopover anchorRef={expBadgeRef} popRef={expPopRef} onClose={() => setExpOpen(false)} navigate={navigate} />,
                document.body
            )}

            <div className={`${collapsed ? "p-2" : "p-4"} border-t border-slate-200`}>
                <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3 mb-3"}`}>
                    <div className="h-9 w-9 rounded-full bg-teal-50 text-[#25908A] flex items-center justify-center font-heading font-semibold border border-teal-100 overflow-hidden shrink-0">
                        {user?.picture ? (
                            <img src={user.picture} alt="" className="h-full w-full object-cover" />
                        ) : (
                            initials
                        )}
                    </div>
                    {!collapsed && (
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                        <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                    )}
                </div>
                {!collapsed && (
                <div className="flex items-center justify-between gap-2">
                    {isActualSuperAdmin ? (
                        <div className="flex-1 min-w-0">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1">View as</div>
                            <div className="inline-flex p-0.5 rounded-lg bg-slate-100 border border-slate-200">
                                {[
                                    { key: null, label: "Super", cls: "text-purple-700" },
                                    { key: "manager", label: "Manager", cls: "text-blue-700" },
                                    { key: "employee", label: "Employee", cls: "text-slate-700" },
                                ].map((opt) => {
                                    const isCurrent = (user?.role_override || null) === opt.key;
                                    return (
                                        <button
                                            key={opt.label}
                                            type="button"
                                            disabled={switchingRole}
                                            onClick={() => switchRoleTo(opt.key)}
                                            data-testid={`role-switch-${opt.label.toLowerCase()}`}
                                            className={`px-2 h-6 text-[10px] font-bold uppercase tracking-wider rounded-md transition-colors ${
                                                isCurrent
                                                    ? `bg-white shadow-sm ${opt.cls}`
                                                    : "text-slate-500 hover:text-slate-700"
                                            } ${switchingRole ? "opacity-50 cursor-wait" : ""}`}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleBadge.cls}`}>
                            <ShieldCheck className="h-3 w-3" />
                            {roleBadge.label}
                        </span>
                    )}
                    <button
                        onClick={() => { onItemClick?.(); logout(); }}
                        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-lg text-xs text-slate-500 hover:bg-slate-100 transition-colors lg:hidden"
                        data-testid="mobile-logout"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Logout
                    </button>
                </div>
                )}
            </div>
        </>
    );

    return (
        <div className="min-h-screen flex bg-slate-50">
            {/* Desktop Sidebar Rail (72 px) — inner container expands to
             *   264 px on hover as an OVERLAY so the main content stays put.
             *   Full labels + badges only when expanded; icons always. */}
            <aside
                className="w-[72px] hidden lg:block sticky top-0 h-screen z-30 shrink-0"
                data-testid="sidebar"
                onMouseEnter={openRail}
                onMouseLeave={closeRail}
            >
                <div
                    className={`absolute inset-y-0 left-0 flex flex-col bg-white border-r border-slate-200 transition-[width,box-shadow] duration-200 ease-out ${railHover ? "w-[264px] shadow-2xl" : "w-[72px]"}`}
                    onFocusCapture={openRail}
                    onBlurCapture={closeRail}
                >
                    <SidebarInner collapsed={!railHover} />
                </div>
            </aside>

            {/* Mobile Drawer */}
            {drawerOpen && (
                <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-drawer">
                    <div
                        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setDrawerOpen(false)}
                    />
                    <aside className="absolute top-0 left-0 bottom-0 w-72 bg-white border-r border-slate-200 flex flex-col animate-slide-in-left">
                        <button
                            onClick={() => setDrawerOpen(false)}
                            aria-label="Close menu"
                            data-testid="mobile-drawer-close"
                            className="absolute top-3 right-3 h-9 w-9 rounded-lg inline-flex items-center justify-center text-slate-500 hover:bg-slate-100 z-10"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <SidebarInner onItemClick={() => setDrawerOpen(false)} />
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Topbar — sticky, slides UP on scroll-down and back on
                 *   scroll-up so the search + cost + XP row gives back
                 *   vertical space while the user reads. */}
                <header
                    data-testid="topbar"
                    className={`sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 gap-2 transition-transform duration-200 ease-out ${headerHidden ? "-translate-y-full" : "translate-y-0"}`}
                >
                    <div className="flex items-center gap-2 lg:hidden min-w-0">
                        <button
                            onClick={() => setDrawerOpen(true)}
                            aria-label="Open menu"
                            data-testid="mobile-menu-btn"
                            className="h-10 w-10 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                        <div className="h-8 w-8 rounded-lg bg-[#2EB4AC] text-white flex items-center justify-center font-heading font-bold shrink-0">O</div>
                        <div className="text-base font-heading font-bold text-slate-900 truncate tracking-tight">Odyssey</div>
                    </div>
                    <div className="hidden lg:block font-heading text-lg text-slate-900">
                        {location.pathname === "/learning" ? null : (
                            <>Hello, <span className="font-semibold">{user?.name?.split(" ")[0]}</span></>
                        )}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="hidden sm:inline-flex"><CommandPaletteTrigger /></div>
                        {user?.role_override ? (
                            <span
                                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-amber-50 text-amber-800 border-amber-300"
                                title="Developer view — click 'Super' in the sidebar to revert"
                                data-testid="role-override-banner"
                            >
                                <ShieldCheck className="h-3 w-3" />
                                Viewing as {roleBadge.label}
                            </span>
                        ) : (
                            <span
                                className={`hidden md:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleBadge.cls}`}
                                title={roleBadge.sublabel ? `${roleBadge.label} · ${roleBadge.sublabel}` : roleBadge.label}
                            >
                                <ShieldCheck className="h-3 w-3" />
                                <span className="font-semibold">{roleBadge.label}</span>
                                {roleBadge.sublabel && (
                                    <span className="hidden lg:inline opacity-80 max-w-[12ch] truncate">· {roleBadge.sublabel}</span>
                                )}
                            </span>
                        )}
                        <div className="hidden sm:inline-flex"><CostTrackerWidget /></div>
                        <LevelBadge />
                        <button
                            onClick={() => navigate(user?.user_id ? `/employees/${user.user_id}` : "/profile")}
                            data-testid="topbar-avatar"
                            className="h-9 w-9 rounded-full bg-teal-50 text-[#25908A] flex items-center justify-center font-heading font-semibold border border-teal-100 overflow-hidden hover:ring-2 hover:ring-[#2EB4AC]/30 transition-shadow"
                            aria-label="Open my profile"
                            title="View my profile"
                        >
                            {user?.picture ? (
                                <img src={user.picture} alt="" className="h-full w-full object-cover" />
                            ) : (
                                initials
                            )}
                        </button>
                        <button
                            onClick={logout}
                            data-testid="logout-button"
                            className="hidden lg:inline-flex items-center gap-2 h-10 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                            aria-label="Logout"
                        >
                            <LogOut className="h-4 w-4" />
                            <span>Logout</span>
                        </button>
                    </div>
                </header>

                <main key={location.pathname} className="flex-1 p-4 sm:p-6 lg:p-8 animate-fade-in-up">
                    <Outlet />
                    <ProfileOnboarding />
                </main>
            </div>

            <CommandPalette />
            <GlobalSmartBot />
            {!location.pathname.startsWith("/learning") && <FloatingNewsWidget />}
        </div>
    );
};

export default Layout;
